/**
 * Admin Orca Defaults RPC definitions (INF-858)
 *
 * The operator-facing, READ-ONLY snapshot behind the Orca Admin "Platform
 * defaults" page (`/orca/defaults`): every non-secret `ORCA_*` knob (its
 * effective value, default, and whether that value came from the environment or
 * the default), the team → repo routes (registry `team_keys` ∪
 * `ORCA_TEAM_REPO_MAP`), the label vocabulary, the Linear state vocabulary, and
 * the three kill switches.
 *
 * One admin-gated method, `admin.orcaDefaults.get`, with no payload. It is
 * admin-only (`AdminMiddleware`) because even non-secret knobs (worker
 * image/tag, kill-switch state, timeouts) describe the production control
 * plane — this must never mount on a public or `/health`-style route.
 *
 * SECURITY: no secret, token, key, or webhook URL may ever appear in the
 * response. The allowlist ({@link OrcaDefaultKnob} names, defined in the
 * router) is the ONLY source of knob names, and values are plain strings — never
 * `Config.redacted` reads. See `orca-defaults.router.ts` for the enforced
 * allowlist and its load-time secret-name guard.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { OrcaTaskPhase } from "@fm/db/models/OrcaTask";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";

// ============================================================================
// Response schemas
// ============================================================================

/**
 * Where a knob's effective value came from: an explicit environment value
 * (`env`) or the compiled-in default (`default`).
 *
 * @since 1.0.0
 */
export const OrcaDefaultKnobSource = Schema.Literal("env", "default");
export type OrcaDefaultKnobSource = Schema.Schema.Type<typeof OrcaDefaultKnobSource>;

/**
 * One allowlisted `ORCA_*` knob. `value` is the effective value as text (`null`
 * only when the knob is unset AND has no default, e.g.
 * `ORCA_WORKER_MEMORY_LIMIT`); `defaultValue` is the default as written at the
 * knob's real read site; `source` reflects whether an env value was present.
 * `group` buckets the knob for the admin UI.
 *
 * @since 1.0.0
 */
export const OrcaDefaultKnob = Schema.Struct({
  name: Schema.String,
  value: Schema.NullOr(Schema.String),
  defaultValue: Schema.NullOr(Schema.String),
  source: OrcaDefaultKnobSource,
  description: Schema.String,
  group: Schema.Literal(
    "kill",
    "dispatch",
    "worker",
    "timeouts",
    "reconciler",
    "runners",
    "linear",
    "identity",
    "archive",
  ),
});
export type OrcaDefaultKnob = Schema.Schema.Type<typeof OrcaDefaultKnob>;

/**
 * A resolved team → repo route. `source` is `registry` for a route derived from
 * an `orca_repos.team_keys` row, or `env` for one filled from
 * `ORCA_TEAM_REPO_MAP`. Registry wins on conflict (mirrors the dispatcher).
 *
 * @since 1.0.0
 */
export const OrcaTeamRoute = Schema.Struct({
  teamKey: Schema.String,
  repo: Schema.String,
  source: Schema.Literal("registry", "env"),
});
export type OrcaTeamRoute = Schema.Schema.Type<typeof OrcaTeamRoute>;

/**
 * One model-label → Claude Code `--model` id mapping.
 *
 * @since 1.0.0
 */
export const OrcaModelLabel = Schema.Struct({
  label: Schema.String,
  model: Schema.String,
});
export type OrcaModelLabel = Schema.Schema.Type<typeof OrcaModelLabel>;

/**
 * One orca phase → Linear workflow-state name (`null` when orca must not push a
 * state, e.g. `CANCELED_IN_LINEAR`).
 *
 * @since 1.0.0
 */
export const OrcaPhaseState = Schema.Struct({
  phase: OrcaTaskPhase,
  stateName: Schema.NullOr(Schema.String),
});
export type OrcaPhaseState = Schema.Schema.Type<typeof OrcaPhaseState>;

/**
 * The label vocabulary: the `repo:` selector prefix, the orca-exclude label,
 * the default builder model, the model-label table, and the agent-CLI literals.
 *
 * @since 1.0.0
 */
export const OrcaLabelVocabulary = Schema.Struct({
  repoPrefix: Schema.String,
  exclude: Schema.String,
  defaultModel: Schema.String,
  models: Schema.Array(OrcaModelLabel),
  agents: Schema.Array(Schema.String),
});
export type OrcaLabelVocabulary = Schema.Schema.Type<typeof OrcaLabelVocabulary>;

/**
 * The Linear vocabulary: the trigger status, the per-phase state map, and the
 * project active/completed status names.
 *
 * @since 1.0.0
 */
export const OrcaLinearVocabulary = Schema.Struct({
  triggerStatus: Schema.String,
  phaseStates: Schema.Array(OrcaPhaseState),
  projectActiveStatus: Schema.String,
  projectCompletedStatus: Schema.String,
});
export type OrcaLinearVocabulary = Schema.Schema.Type<typeof OrcaLinearVocabulary>;

/**
 * The three orchestration kill switches.
 *
 * @since 1.0.0
 */
export const OrcaKillSwitches = Schema.Struct({
  orchestratorEnabled: Schema.Boolean,
  reconcilerEnabled: Schema.Boolean,
  verifyEnabled: Schema.Boolean,
});
export type OrcaKillSwitches = Schema.Schema.Type<typeof OrcaKillSwitches>;

/**
 * The full allowlisted, read-only platform-defaults snapshot.
 *
 * @since 1.0.0
 */
export const OrcaDefaultsSnapshot = Schema.Struct({
  environment: Schema.String,
  defaultRepo: Schema.String,
  gateRepo: Schema.String,
  knobs: Schema.Array(OrcaDefaultKnob),
  teamRoutes: Schema.Array(OrcaTeamRoute),
  labels: OrcaLabelVocabulary,
  linear: OrcaLinearVocabulary,
  kill: OrcaKillSwitches,
});
export type OrcaDefaultsSnapshot = Schema.Schema.Type<typeof OrcaDefaultsSnapshot>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca Defaults RPC group. The single endpoint requires admin role via
 * `AdminMiddleware`; the tag is the full dot-namespaced name (mirroring
 * {@link AdminOrcaEventsRpcs}).
 *
 * @since 1.0.0
 */
export class AdminOrcaDefaultsRpcs extends RpcGroup.make(
  Rpc.make("admin.orcaDefaults.get", {
    success: OrcaDefaultsSnapshot,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
